import React, { useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import {
  LayoutDashboard,
  Users,
  Mic2,
  Video,
  Building2,
  ShoppingCart,
  Settings,
  LogOut,
  ChevronRight,
  ChevronDown,
  Shield,
  Menu,
  X,
  UserPlus,
  BarChart3,
  Mail,
  FileText,
  Send,
  Layout,
  MailX,
  Clock,
  Building,
  Tag,
  PlayCircle,
  BookOpen,
  PenTool,
  Image as ImageIcon,
  Rocket,
  Receipt,
  Package,
} from 'lucide-react';
import { useState } from 'react';

// Logo SVG Component
const Logo = ({ className = "" }) => (
  <svg className={className} viewBox="0 0 200 50" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M10 25 L25 10 L40 25 L25 18 L10 25Z" fill="#1B4F72" />
    <path d="M25 18 L40 25 L25 40 L25 32 L15 25 L25 18Z" fill="#D4A03A" fillOpacity="0.9" />
    <text x="50" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="700" fill="#1B4F72">GRC</text>
    <text x="95" y="32" fontFamily="Space Grotesk, sans-serif" fontSize="22" fontWeight="400" fill="#64748B">educators</text>
  </svg>
);

const navItems = [
  { path: '/admin', icon: LayoutDashboard, label: 'Dashboard', exact: true },
  { path: '/admin/users', icon: Users, label: 'Users' },
  { path: '/admin/speakers', icon: Mic2, label: 'Speakers' },
  { path: '/admin/webinars', icon: Video, label: 'Webinars' },
  { path: '/admin/seminars', icon: Building2, label: 'Seminars' },
  { path: '/admin/series', icon: PlayCircle, label: 'Series' },
  { path: '/admin/bundles', icon: Package, label: 'Bundles' },
  { path: '/admin/recordings', icon: PlayCircle, label: 'Recordings' },
  { path: '/admin/industries', icon: Building2, label: 'Industries' },
  { path: '/admin/gallery', icon: ImageIcon, label: 'Media Library' },
  { path: '/admin/orders', icon: ShoppingCart, label: 'Orders' },
  { path: '/admin/invoices', icon: Receipt, label: 'Invoices' },
  {
    icon: BarChart3,
    label: 'Reports',
    isGroup: true,
    children: [
      { path: '/admin/reports', icon: FileText, label: 'Sales Report' },
    ]
  },
  { path: '/admin/go-live', icon: Rocket, label: 'Go-Live Management' },
  { path: '/admin/landing-pages', icon: Layout, label: 'Landing Pages' },
  { path: '/admin/leads', icon: UserPlus, label: 'CRM - Leads' },
  { path: '/admin/corporate-accounts', icon: Building, label: 'Corporate Accounts' },
  { path: '/admin/promo-codes', icon: Tag, label: 'Promo Codes' },
  { 
    icon: BookOpen, 
    label: 'Content',
    isGroup: true,
    children: [
      { path: '/admin/articles', icon: FileText, label: 'Articles' },
      { path: '/admin/content-manager', icon: PenTool, label: 'Page Content' },
    ]
  },
  { path: '/admin/analytics', icon: BarChart3, label: 'Analytics' },
  { 
    icon: Mail, 
    label: 'Email Marketing',
    isGroup: true,
    children: [
      { path: '/admin/emails', icon: Send, label: 'Campaigns' },
      { path: '/admin/email-templates', icon: FileText, label: 'Templates' },
      { path: '/admin/email-builder', icon: Layout, label: 'Email Builder' },
      { path: '/admin/email-scheduling', icon: Clock, label: 'Scheduling' },
      { path: '/admin/unsubscribes', icon: MailX, label: 'Unsubscribes' },
    ]
  },
  { path: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout() {
  const { user, logout, loading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [expandedGroups, setExpandedGroups] = useState(['Email Marketing', 'Content', 'Reports']);

  // Check if user is admin
  useEffect(() => {
    if (!loading) {
      if (!user) {
        navigate('/login');
      } else if (user.role !== 'admin' && user.role !== 'super_admin') {
        navigate('/dashboard');
      }
    }
  }, [user, navigate, loading]);

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-zinc-900"></div>
      </div>
    );
  }

  if (!user || (user.role !== 'admin' && user.role !== 'super_admin')) {
    return null;
  }

  const isActive = (path, exact = false) => {
    if (exact) {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const isGroupActive = (children) => {
    return children?.some(child => isActive(child.path));
  };

  const toggleGroup = (label) => {
    setExpandedGroups(prev => 
      prev.includes(label) 
        ? prev.filter(g => g !== label)
        : [...prev, label]
    );
  };

  const renderNavItem = (item) => {
    if (item.isGroup) {
      const isExpanded = expandedGroups.includes(item.label);
      const groupActive = isGroupActive(item.children);
      
      return (
        <li key={item.label}>
          <button
            onClick={() => toggleGroup(item.label)}
            className={`
              w-full flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all
              ${groupActive
                ? 'bg-zinc-100 text-zinc-900'
                : 'text-slate-600 hover:bg-zinc-100 hover:text-zinc-900'
              }
            `}
          >
            <item.icon className="w-5 h-5" />
            <span>{item.label}</span>
            <ChevronDown className={`w-4 h-4 ml-auto transition-transform ${isExpanded ? 'rotate-180' : ''}`} />
          </button>
          {isExpanded && (
            <ul className="mt-1 ml-6 space-y-1 border-l-2 border-slate-200 pl-4">
              {item.children.map(child => (
                <li key={child.path}>
                  <Link
                    to={child.path}
                    onClick={() => setSidebarOpen(false)}
                    className={`
                      flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium transition-all
                      ${isActive(child.path)
                        ? 'bg-zinc-900 text-white shadow-sm'
                        : 'text-slate-600 hover:bg-zinc-100 hover:text-zinc-900'
                      }
                    `}
                  >
                    <child.icon className="w-4 h-4" />
                    <span>{child.label}</span>
                  </Link>
                </li>
              ))}
            </ul>
          )}
        </li>
      );
    }

    return (
      <li key={item.path}>
        <Link
          to={item.path}
          onClick={() => setSidebarOpen(false)}
          className={`
            flex items-center gap-3 px-4 py-3 rounded-xl font-medium transition-all
            ${isActive(item.path, item.exact)
              ? 'bg-navy-600 text-white shadow-md'
              : 'text-slate-600 hover:bg-zinc-100 hover:text-zinc-900'
            }
          `}
          data-testid={`admin-nav-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
        >
          <item.icon className="w-5 h-5" />
          <span>{item.label}</span>
          {isActive(item.path, item.exact) && (
            <ChevronRight className="w-4 h-4 ml-auto" />
          )}
        </Link>
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="admin-layout">
      {/* Mobile Sidebar Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}

      {/* Sidebar */}
      <aside className={`
        fixed top-0 left-0 z-50 h-full w-72 bg-white border-r border-slate-200 
        transform transition-transform duration-300 ease-in-out
        lg:translate-x-0
        ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}
      `}>
        {/* Logo */}
        <div className="h-16 flex items-center justify-between px-6 border-b border-slate-200">
          <Link to="/admin" className="flex items-center gap-2">
            <Logo className="h-10 w-auto" />
          </Link>
          <button 
            className="lg:hidden text-slate-500 hover:text-slate-700"
            onClick={() => setSidebarOpen(false)}
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Admin Badge */}
        <div className="px-6 py-4">
          <div className="flex items-center gap-2 px-3 py-2 bg-white border border-zinc-200 rounded-xl">
            <Shield className="w-5 h-5 text-emerald-600" />
            <span className="text-sm font-semibold text-zinc-900">Admin Panel</span>
          </div>
        </div>

        {/* Navigation */}
        <nav className="px-4 py-2 overflow-y-auto" style={{ maxHeight: 'calc(100vh - 280px)' }}>
          <ul className="space-y-1">
            {navItems.map((item) => renderNavItem(item))}
          </ul>
        </nav>

        {/* User Section */}
        <div className="absolute bottom-0 left-0 right-0 p-4 border-t border-slate-200 bg-white">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 rounded-full bg-zinc-900 flex items-center justify-center">
              <span className="text-white font-semibold">
                {user.first_name?.[0]}{user.last_name?.[0]}
              </span>
            </div>
            <div className="flex-1 min-w-0">
              <p className="font-medium text-slate-800 truncate">
                {user.first_name} {user.last_name}
              </p>
              <p className="text-xs text-slate-500 truncate">{user.email}</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Link to="/dashboard" className="flex-1">
              <Button variant="outline" size="sm" className="w-full rounded-lg">
                <Settings className="w-4 h-4 mr-2" />
                Dashboard
              </Button>
            </Link>
            <Button 
              variant="outline" 
              size="sm" 
              onClick={logout}
              className="text-red-600 hover:text-red-700 hover:bg-red-50 rounded-lg"
            >
              <LogOut className="w-4 h-4" />
            </Button>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="lg:ml-72">
        {/* Top Header */}
        <header className="sticky top-0 z-30 h-16 bg-white border-b border-slate-200 flex items-center px-4 lg:px-8">
          <button 
            className="lg:hidden mr-4 text-slate-500 hover:text-slate-700"
            onClick={() => setSidebarOpen(true)}
          >
            <Menu className="w-6 h-6" />
          </button>
          
          <div className="flex-1">
            <h1 className="font-heading text-xl font-bold text-navy-700">
              {navItems.find(item => isActive(item.path, item.exact))?.label || 'Admin'}
            </h1>
          </div>

          <Link to="/" className="text-sm text-slate-500 hover:text-emerald-600">
            View Site →
          </Link>
        </header>

        {/* Page Content */}
        <main className="p-4 lg:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
